/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, defineMessage, useIntl} from 'react-intl'
import {Route, Switch, useRouteMatch, Redirect} from 'react-router'
import {
    Accordion,
    AccordionButton,
    AccordionItem,
    AccordionPanel,
    Box,
    Button,
    Grid,
    Stack,
    Heading,
    Text,
    List,
    ListItem
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Show, Hide} from '@chakra-ui/react'
import Seo from '@salesforce/retail-react-app/app/components/seo'
import Link from '@salesforce/retail-react-app/app/components/link'
import {ChevronDownIcon, ChevronUpIcon} from '../../components/custom-icons'
import AccountDetail from './profile'
import AccountAddresses from './addresses'
import AccountOrders from './orders'
import AccountWishlist from './wishlist'
import AccountRepair from './repair'
import {useLocation} from 'react-router-dom'

import {messages, navLinks} from './constant'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import LoadingSpinner from '../../components/loading-spinner'
import useMultiSite from '../../hooks/use-multi-site'
import useEinstein from '../../hooks/use-einstein'
import {useAuthHelper, AuthHelpers} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import AccountPrivacy from './account-privacy'
import useUpdateShopperContext from '@app/hooks/use-update-shopper-context'

const onClient = typeof window !== 'undefined'

const LogoutButton = ({onClick}) => {
    const {formatMessage} = useIntl()
    return (
        <Button onClick={onClick} variant="menuLink" cursor={'pointer'}>
            {formatMessage({
                defaultMessage: 'Log Out',
                id: 'account.logout_button.button.log_out'
            })}
        </Button>
    )
}
LogoutButton.propTypes = {
    onClick: PropTypes.func.isRequired
}

const NavigationList = ({navLinks, onSignoutClick, messages}) => {
    const {formatMessage} = useIntl()

    return (
        <List
            data-testid="account-detail-nav"
            display="flex"
            flexDirection="column"
            align="flex-start"
            width="full"
            spacing={0}
        >
            {navLinks.map((link) => {
                const LinkIcon = link.icon
                return (
                    <ListItem key={link.name}>
                        <Button
                            as={Link}
                            to={`/account${link.path}`}
                            useNavLink={true}
                            variant="menuLink"
                            leftIcon={<LinkIcon boxSize={3} />}
                            iconSpacing={12}
                            aria-current={link.isActive ? 'page' : undefined}
                        >
                            <Text as="span">{formatMessage(messages[link.name])}</Text>
                        </Button>
                    </ListItem>
                )
            })}
            <ListItem key="logout" marginTop={16} borderTop="1px solid" borderColor="borderGray">
                <LogoutButton onClick={onSignoutClick} />
            </ListItem>
        </List>
    )
}

NavigationList.propTypes = {
    navLinks: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            path: PropTypes.string.isRequired,
            icon: PropTypes.elementType.isRequired,
            isActive: PropTypes.bool
        })
    ).isRequired,
    onSignoutClick: PropTypes.func.isRequired,
    messages: PropTypes.object.isRequired
}

const NAV_ARIA_LABEL = defineMessage({
    defaultMessage: 'Account Navigation',
    id: 'account_page.nav.aria_label'
})

const Account = () => {
    const {path} = useRouteMatch()
    const {formatMessage} = useIntl()
    const {data: customer} = useCurrentCustomer()
    const {isRegistered, customerType} = customer

    const logout = useAuthHelper(AuthHelpers.Logout)
    const location = useLocation()
    const navigate = useNavigation()

    const [mobileNavIndex, setMobileNavIndex] = useState(-1)
    const [showLoading, setShowLoading] = useState(false)

    const einstein = useEinstein()
    const {updateShopperContextAfterLogout} = useUpdateShopperContext()

    const {buildURLWithUTM} = useMultiSite()
    /**************** Einstein ****************/
    useEffect(() => {
        einstein.sendViewPage(location.pathname)
    }, [location])

    const onSignoutClick = async () => {
        setShowLoading(true)
        const logoutResult = await logout.mutateAsync()
        await updateShopperContextAfterLogout()
        navigate('/login')
    }

    // If we have customer data and they are not registered, push to login page
    // Using Redirect allows us to store the directed page to location
    // so we can direct users back after they are successfully log in
    if (customerType !== null && !isRegistered && onClient) {
        const path = buildURLWithUTM('/login')
        return <Redirect to={{...path, state: {directedFrom: '/account'}}} />
    }

    return (
        <Box
            data-testid={isRegistered && isHydrated() ? 'account-page' : 'account-page-skeleton'}
            layerStyle="accountpage"
        >
            <Seo
                title={formatMessage({
                    id: 'account_page.seo.title',
                    defaultMessage: 'My Account'
                })}
                description={formatMessage({
                    id: 'account_page.seo.desc',
                    defaultMessage: 'Customer Account Page'
                })}
            />

            {showLoading && (
                <LoadingSpinner isBodyLocked={true} wrapperStyles={{height: '100dvh'}} />
            )}
            <Grid templateColumns={{base: '1fr', lg: '383px 1fr'}} gap={{base: 40, lg: 57}}>
                <Box>
                    {/* Small screen nav accordion */}
                    <Hide above="lg">
                        <Accordion
                            as="nav"
                            aria-label={formatMessage(NAV_ARIA_LABEL)}
                            bg="white"
                            allowToggle={true}
                            reduceMotion={true}
                            index={mobileNavIndex}
                            onChange={setMobileNavIndex}
                        >
                            <AccordionItem
                                borderTop="1px solid"
                                borderBottom="1px solid"
                                borderColor="borderGray"
                            >
                                {({isExpanded}) => (
                                    <>
                                        <Heading as="h2">
                                            <AccordionButton
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                px={16}
                                                py={24}
                                            >
                                                <Text
                                                    as="span"
                                                    align="left"
                                                    textStyle="eyeBrowSmall"
                                                    textTransform="uppercase"
                                                >
                                                    <FormattedMessage
                                                        defaultMessage="My Account"
                                                        id="account.accordion.button.my_account"
                                                    />
                                                </Text>
                                                {isExpanded ? (
                                                    <ChevronUpIcon boxSize="12px" />
                                                ) : (
                                                    <ChevronDownIcon boxSize="12px" />
                                                )}
                                            </AccordionButton>
                                        </Heading>

                                        <AccordionPanel paddingTop={0} px={16} paddingBottom={8}>
                                            <NavigationList
                                                navLinks={navLinks}
                                                onSignoutClick={onSignoutClick}
                                                messages={messages}
                                            />
                                        </AccordionPanel>
                                    </>
                                )}
                            </AccordionItem>
                        </Accordion>
                    </Hide>

                    {/* Large screen nav sidebar */}
                    <Show above="lg">
                        <Stack
                            as="nav"
                            spacing={16}
                            bg="white"
                            p={16}
                            width="full"
                            align="flex-start"
                            border="1px solid"
                            borderColor="borderGray"
                            aria-label={formatMessage(NAV_ARIA_LABEL)}
                        >
                            <Text
                                as="h2"
                                display="block"
                                textStyle="eyeBrowSmall"
                                textTransform="uppercase"
                            >
                                <FormattedMessage
                                    defaultMessage="My Account"
                                    id="account.heading.my_account"
                                />
                            </Text>

                            <NavigationList
                                navLinks={navLinks}
                                onSignoutClick={onSignoutClick}
                                messages={messages}
                            />
                        </Stack>
                    </Show>
                </Box>

                <Box mx={[16, 16, 16, 0]}>
                    <Switch>
                        <Route exact path={path}>
                            <AccountDetail />
                        </Route>
                        <Route exact path={`${path}/addresses`}>
                            <AccountAddresses />
                        </Route>
                        <Route path={`${path}/orders`}>
                            <AccountOrders />
                        </Route>
                        <Route exact path={`${path}/privacy`}>
                            <AccountPrivacy />
                        </Route>
                        <Route exact path={`${path}/wishlist`}>
                            <AccountWishlist />
                        </Route>
                        <Route exact path={`${path}/repair`}>
                            {/* @TODO: update repair component */}
                            <AccountRepair />
                        </Route>
                    </Switch>
                </Box>
            </Grid>
        </Box>
    )
}

Account.getTemplateName = () => 'account'

Account.propTypes = {
    match: PropTypes.object
}

export default Account
